import type { WorkflowSummaryHandlers } from './useWorkflowSummaryHandlers.types';
import type { WorkflowSummaryProps } from '../WorkflowSummary.types';
import type { WorkflowSummaryData } from './useWorkflowSummaryData.types';

import * as uiLib from '@compliance.ai/web-components';
import { delay } from 'utils/timeout';
import { PRINT_CONTENT_ARRANGEMENT_DELAY } from '../WorkflowSummary.constants';

import { useCallback } from 'react';
import { usePrint } from '@compliance.ai/web-components';

export const useWorkflowSummaryHandlers = ({
  props,
  refs,
  localState,
  localActions,
  reduxState
}: {
  props: Pick<WorkflowSummaryProps, 'workflow_name'>;
  refs: WorkflowSummaryData['refs'];
  localState: WorkflowSummaryData['localState'];
  localActions: WorkflowSummaryData['localActions'];
  reduxState: WorkflowSummaryData['reduxState'];
}): WorkflowSummaryHandlers => {
  const printSummary = usePrint({
    bodyClass: 'workflow-summary__pdf-body',
    contentRef: refs.componentToPrint,
    documentTitle: `${props.workflow_name} - ${uiLib.formatDate(new Date(), {
      passedTimezone: uiLib.getLocalTimezone(),
      timezone: uiLib.getLocalTimezone()
    })}`,
    onBeforePrint: async () => {
      await delay(PRINT_CONTENT_ARRANGEMENT_DELAY);
    }
  });

  const handleSectionToggle: WorkflowSummaryHandlers['handleSectionToggle'] =
    sectionType => isExpanded => {
      localActions.setIsSectionExpanded(sections => ({
        ...sections,
        [sectionType]: isExpanded
      }));
      localActions.setIsSectionExpandAnimationCompleted(sections => ({
        ...sections,
        [sectionType]: !isExpanded ? false : sections[sectionType]
      }));
    };

  const handleSectionExpandAnimationComplete: WorkflowSummaryHandlers['handleSectionExpandAnimationComplete'] =
    sectionType => () => {
      localActions.setIsSectionExpandAnimationCompleted(sections => ({
        ...sections,
        [sectionType]: true
      }));
    };

  const handlePrintPreparation: WorkflowSummaryHandlers['handlePrintPreparation'] = () => {
    localActions.setIsPreparingForPrint(true);
    localActions.setIsSectionExpanded(
      sections =>
        Object.fromEntries(
          Object.entries(sections).map(([section]) => [section, true])
        ) as (typeof localState)['isSectionExpanded']
    );

    if (refs.animationFrameId.current) {
      cancelAnimationFrame(refs.animationFrameId.current);
      refs.animationFrameId.current = null;
    }
    refs.lastScrollHeight.current = null;

    if (!reduxState.doesDocumentHasSentences) {
      localActions.setIsSectionExpandAnimationCompleted(
        sections =>
          Object.fromEntries(
            Object.entries(sections).map(([section]) => [section, true])
          ) as (typeof localState)['isSectionExpandAnimationCompleted']
      );

      localActions.setIsSentenceNotesFetchCompleted(
        sentenceNoteTypes =>
          Object.fromEntries(
            Object.entries(sentenceNoteTypes).map(([type]) => [type, true])
          ) as (typeof localState)['isSentenceNotesFetchCompleted']
      );
    }
  };

  const handlePrintableContentReady: WorkflowSummaryHandlers['handlePrintableContentReady'] =
    useCallback(() => {
      if (
        localState.isPreparingForPrint &&
        Object.values(localState.isSectionExpandAnimationCompleted).every(Boolean) &&
        refs.componentToPrint.current
      ) {
        const checkContentStability = () => {
          const currentScrollHeight = refs.componentToPrint.current?.scrollHeight || 0;
          if (refs.lastScrollHeight.current === currentScrollHeight) {
            if (refs.animationFrameId.current) {
              cancelAnimationFrame(refs.animationFrameId.current);
              refs.animationFrameId.current = null;
            }
            localActions.setIsPreparingForPrint(false);
            localActions.setReadyToBePrintedState(true);
          } else {
            refs.lastScrollHeight.current = currentScrollHeight;
            refs.animationFrameId.current = requestAnimationFrame(checkContentStability);
          }
        };
        if (!refs.animationFrameId.current) {
          refs.animationFrameId.current = requestAnimationFrame(checkContentStability);
        }
      }
    }, [
      localActions,
      localState.isPreparingForPrint,
      localState.isSectionExpandAnimationCompleted,
      refs.animationFrameId,
      refs.componentToPrint,
      refs.lastScrollHeight
    ]);

  const handlePrint: WorkflowSummaryHandlers['handlePrint'] = useCallback(() => {
    if (localState.isReadyToBePrinted && refs.componentToPrint.current) {
      printSummary();
      localActions.setReadyToBePrintedState(false);
      refs.lastScrollHeight.current = null;
    }
  }, [
    localState.isReadyToBePrinted,
    refs.componentToPrint,
    refs.lastScrollHeight,
    printSummary,
    localActions
  ]);

  const handleSentenceNotesFetchCompletion: WorkflowSummaryHandlers['handleSentenceNotesFetchCompletion'] =
    type => () => {
      localActions.setIsSentenceNotesFetchCompleted(isSentenceNotesFetchCompleted => ({
        ...isSentenceNotesFetchCompleted,
        [type]: true
      }));
    };

  return {
    handleSectionToggle,
    handleSectionExpandAnimationComplete,
    handlePrint,
    handlePrintableContentReady,
    handlePrintPreparation,
    handleSentenceNotesFetchCompletion
  };
};
